/**
 * API Client pour la gestion des clients
 */
import { safeFetch, safeJsonParse, getAuthHeaders } from './api';
import type { Client, ClientContact, ContactType } from '../types/models';
// Re-export types for consumers
export type { Client, ClientContact, ContactType };
// Mappers supprimés: API et frontend alignés camelCase

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";


export interface ClientsResponse {
  clients: Client[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

/**
 * API Clients
 */
export const clientsAPI = {
  /**
   * Récupérer tous les clients avec filtres optionnels
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    activeOnly?: boolean;
    clientType?: string[];
  }): Promise<ClientsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.activeOnly) queryParams.append('activeOnly', 'true');
    if (params?.clientType && params.clientType.length > 0) {
      queryParams.append('clientType', params.clientType.join(','));
    }

    const url = `${API_URL}/catalog/clients${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await safeFetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des clients : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);

    // Format standard backend V2 UNIQUEMENT: {success: true, data: [...], meta: {...}}
    if (!apiResponse.success || !apiResponse.data) {
      throw new Error(apiResponse.error || 'Format de réponse API incorrect');
    }

    return {
      clients: Array.isArray(apiResponse.data) ? apiResponse.data : [],
      pagination: apiResponse.meta || {
        total: apiResponse.data.length || 0,
        page: params?.page || 1,
        limit: params?.limit || apiResponse.data.length || 0,
        pages: 1,
      },
    };
  },

  /**
   * Récupérer un client par ID
   */
  async getById(clientId: string): Promise<Client> {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération du client : ${response.statusText}`);
    }

    const apiClient = await safeJsonParse(response);
    const client = apiClient.data || apiClient;
    return client;
  },

  /**
   * Créer un nouveau client
   */
  async create(clientData: Partial<Client>): Promise<Client> {

    const response = await safeFetch(`${API_URL}/catalog/clients`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(clientData),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la création du client : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    const client = apiResponse.data || apiResponse;
    return client;
  },

  /**
   * Mettre à jour un client
   */
  async update(clientId: string, clientData: Partial<Client>): Promise<Client> {

    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(clientData),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la mise à jour du client : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    const client = apiResponse.data || apiResponse;
    return client;
  },

  /**
   * Supprimer un client
   */
  async delete(clientId: string): Promise<{ success: boolean }> {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la suppression du client : ${response.statusText}`);
    }

    return safeJsonParse(response);
  },

  /**
   * Activer/Désactiver un client
   * Note: Cette méthode reste pour compatibilité mais is_active n'existe plus dans le modèle
   */
  async toggleStatus(clientId: string): Promise<Client> {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}/toggle`, {
      method: "PATCH",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors du changement de statut du client : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    const client = apiResponse.data || apiResponse;
    return client;
  },

  // ============================================
  // CONTACT MANAGEMENT
  // ============================================

  /**
   * Récupérer tous les contacts d'un client
   */
  async getContacts(clientId: string, activeOnly: boolean = true): Promise<ClientContact[]> {
    const queryParams = new URLSearchParams();
    if (!activeOnly) queryParams.append('activeOnly', 'false');

    const url = `${API_URL}/catalog/clients/${clientId}/contacts${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await safeFetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des contacts : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse || [];
  },

  /**
   * Récupérer un contact par ID
   */
  async getContactById(clientId: string, contactId: string): Promise<ClientContact> {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}/contacts/${contactId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération du contact : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * Créer un nouveau contact
   */
  async createContact(
    clientId: string,
    contactData: {
      type?: ContactType;
      fullName: string;
      email: string;
      phone?: string;
      position?: string;
      department?: string;
      city?: string;
      isPrimary?: boolean;
      notes?: string;
    }
  ): Promise<ClientContact> {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}/contacts`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(contactData),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la création du contact : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * Mettre à jour un contact
   */
  async updateContact(
    clientId: string,
    contactId: string,
    contactData: Partial<{
      type: ContactType;
      fullName: string;
      email: string;
      phone: string;
      position: string;
      department: string;
      city: string;
      isPrimary: boolean;
      notes: string;
    }>
  ): Promise<ClientContact> {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}/contacts/${contactId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(contactData),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la mise à jour du contact : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * Supprimer un contact
   */
  async deleteContact(clientId: string, contactId: string): Promise<{ success: boolean }> {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}/contacts/${contactId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la suppression du contact : ${response.statusText}`);
    }

    return safeJsonParse(response);
  },

  /**
   * Définir un contact comme contact principal
   */
  async setPrimaryContact(clientId: string, contactId: string): Promise<ClientContact> {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}/contacts/${contactId}/set-primary`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la définition du contact principal : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },

  /**
   * Activer/Désactiver un contact
   */
  async toggleContactStatus(clientId: string, contactId: string): Promise<ClientContact> {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}/contacts/${contactId}/toggle-status`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors du changement de statut du contact : ${response.statusText}`);
    }

    const apiResponse = await safeJsonParse(response);
    return apiResponse.data || apiResponse;
  },
};


